import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  ChevronLeft, 
  Music, 
  Plus, 
  X, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  BarChart,
  Users,
  PlayCircle,
  Mic2,
  Guitar,
  Drum,
  Piano,
  Radio,
  Download,
  Edit,
  User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { instrumentOptions, tierOptions } from "@shared/schema";
import type { Performer, Instrument, PerformerWithParticipant } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PageBackground } from "@/components/ui/page-background";
import ParticipantEditModal from "@/components/participant-edit-modal";

interface ParticipantDetailProps {
  jamNightId: number;
}

function convertToCSV(performers: PerformerWithParticipant[]) {
  // Define headers
  const headers = [
    'Name',
    'Instruments',
    'Priority',
    'Looking for Band',
    'Tier',
    'Phone',
    'Email',
    'Instagram',
    'Notes',
    'Has Played',
    'Time on Stage (minutes)'
  ];

  // Convert each performer to CSV row
  const rows = performers.map(performer => [
    performer.name,
    (performer.instruments || []).join('; '),
    performer.priority ? 'Yes' : 'No',
    performer.participant?.lookingForBand ? 'Yes' : 'No',
    performer.tier || '',
    performer.phone || '',
    performer.email || '',
    performer.instagram || '',
    performer.notes || '',
    performer.played ? 'Yes' : 'No',
    performer.timeOnStage?.toString() || '0'
  ]);

  // Combine headers and rows
  const csvContent = [
    headers,
    ...rows
  ].map(row => 
    row.map(cell => 
      // Escape quotes and wrap in quotes if contains comma or quote
      cell.includes(',') || cell.includes('"') 
        ? `"${cell.replace(/"/g, '""')}"` 
        : cell
    ).join(',')
  ).join('\n');

  return csvContent;
}

function ParticipantMetrics({ performers }: { performers: PerformerWithParticipant[] }) {
  const [isMetricsOpen, setIsMetricsOpen] = useState(true);

  // Calculate metrics
  const totalParticipants = performers.length;
  const totalPlayed = performers.filter(p => p.played).length;
  
  // Count participants by instrument
  const instrumentCounts = instrumentOptions.reduce((acc, instrument) => {
    acc[instrument] = performers.filter(p => p.instruments?.includes(instrument)).length;
    return acc;
  }, {} as Record<string, number>);

  // Map instruments to their icons
  const getInstrumentIcon = (instrument: string) => {
    switch (instrument.toLowerCase()) {
      case 'vocals':
        return <Mic2 className="h-4 w-4" />;
      case 'guitar':
        return <Guitar className="h-4 w-4" />;
      case 'drums':
        return <Drum className="h-4 w-4" />;
      case 'piano':
        return <Piano className="h-4 w-4" />;
      default:
        return <Radio className="h-4 w-4" />;
    }
  };

  const handleDownloadCSV = () => {
    const csvContent = convertToCSV(performers);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `participants_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/20 mb-6 relative group"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
      }}>
      {/* Liquid Glass Specular Highlight */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
          pointerEvents: 'none',
        }}
      />
      <Collapsible open={isMetricsOpen} onOpenChange={setIsMetricsOpen}>
        <CollapsibleTrigger asChild>
          <div className="cursor-pointer hover:bg-white/20 transition-colors px-6 py-4 relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart className="h-5 w-5 text-blue-600 drop-shadow-sm" />
                <span className="text-lg font-semibold text-slate-900/90 drop-shadow-sm">Participant Metrics</span>
              </div>
              {isMetricsOpen ? (
                <ChevronUp className="h-4 w-4 text-slate-700/90 drop-shadow-sm" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-700/90 drop-shadow-sm" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-6 pb-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Participants */}
              <div className="bg-white/20 backdrop-blur-xl rounded-xl p-4 border border-white/20 relative group transition-all duration-300 hover:bg-white/25"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-700/90 drop-shadow-sm">Total Participants</div>
                  <Users className="h-5 w-5 text-blue-600 drop-shadow-sm" />
                </div>
                <div className="text-3xl font-bold text-slate-900/90 drop-shadow-sm">{totalParticipants}</div>
              </div>

              {/* Participants Played */}
              <div className="bg-white/20 backdrop-blur-xl rounded-xl p-4 border border-white/20 relative group transition-all duration-300 hover:bg-white/25"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-700/90 drop-shadow-sm">Have Played</div>
                  <PlayCircle className="h-5 w-5 text-green-600 drop-shadow-sm" />
                </div>
                <div className="text-3xl font-bold text-slate-900/90 drop-shadow-sm">
                  {totalPlayed}
                  <span className="text-sm text-slate-600/80 ml-2 font-normal drop-shadow-sm">
                    ({Math.round((totalPlayed / totalParticipants) * 100) || 0}%)
                  </span>
                </div>
              </div>

              {/* Participants by Instrument */}
              <div className="bg-white/20 backdrop-blur-xl rounded-xl p-4 border border-white/20 relative group transition-all duration-300 hover:bg-white/25"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-700/90 drop-shadow-sm">By Instrument</div>
                  <Music className="h-5 w-5 text-purple-600 drop-shadow-sm" />
                </div>
                <div className="space-y-2">
                  {Object.entries(instrumentCounts)
                    .filter(([_, count]) => count > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([instrument, count]) => (
                      <div key={instrument} className="flex justify-between items-center group">
                        <div className="flex items-center space-x-2">
                          <span className="text-slate-500 group-hover:text-slate-800 transition-colors">
                            {getInstrumentIcon(instrument)}
                          </span>
                          <span className="text-sm capitalize">{instrument}</span>
                        </div>
                        <Badge 
                          variant="secondary" 
                          className="bg-white/40 backdrop-blur-sm border border-white/30 text-slate-700/90 shadow-sm transition-colors"
                          style={{
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                          }}
                        >
                          {count}
                        </Badge>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

interface ContactDetailsModalProps {
  performer: PerformerWithParticipant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdatePerformer: (performerId: number, field: keyof Performer, value: any) => void;
  onUpdateParticipant: (participantId: number, field: 'lookingForBand', value: boolean) => void;
}

function ContactDetailsModal({ 
  performer, 
  open, 
  onOpenChange, 
  onUpdatePerformer,
  onUpdateParticipant 
}: ContactDetailsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Contact Details - {performer.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Phone Number
            </label>
            <Input
              value={performer.phone || ""}
              onChange={(e) =>
                onUpdatePerformer(performer.id, "phone", e.target.value)
              }
              placeholder="Enter phone number"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Email Address
            </label>
            <Input
              type="email"
              value={performer.email || ""}
              onChange={(e) =>
                onUpdatePerformer(performer.id, "email", e.target.value)
              }
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Instagram Handle
            </label>
            <Input
              value={performer.instagram || ""}
              onChange={(e) =>
                onUpdatePerformer(performer.id, "instagram", e.target.value)
              }
              placeholder="Enter Instagram handle"
            />
          </div>

          {performer.participant && (
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                Looking for a Band
              </label>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={performer.participant.lookingForBand || false}
                  onCheckedChange={(checked) =>
                    onUpdateParticipant(performer.participant!.id, "lookingForBand", checked)
                  }
                />
                <label className="text-sm text-slate-600">
                  {performer.participant.lookingForBand
                    ? "Looking for a band"
                    : "Not looking for a band"}
                </label>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ParticipantDetail({ jamNightId }: ParticipantDetailProps) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sortedPerformers, setSortedPerformers] = useState<PerformerWithParticipant[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [playedFilter, setPlayedFilter] = useState<string>("all");
  const [showPriorityOnly, setShowPriorityOnly] = useState<boolean>(false);
  const [instrumentFilter, setInstrumentFilter] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<number | null>(null);

  const { data: performers = [], isLoading } = useQuery<PerformerWithParticipant[]>({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
  });

  // Sort performers whenever the data changes (including WebSocket updates)
  useEffect(() => {
    const sorted = [...performers].sort((a, b) => 
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
    setSortedPerformers(sorted);
  }, [performers]);

  const handleDownloadCSV = () => {
    const csvContent = convertToCSV(sortedPerformers);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `participants_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter performers based on selected filters
  const filteredPerformers = sortedPerformers.filter((performer) => {
    // Filter by search term
    if (
      searchTerm &&
      !performer.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }

    // Filter by played status
    if (playedFilter === "played" && !performer.played) return false;
    if (playedFilter === "not-played" && performer.played) return false;

    // Filter by priority status
    if (showPriorityOnly && !performer.priority) return false;

    // Filter by instrument
    if (instrumentFilter.length > 0) {
      const hasMatchingInstrument = performer.instruments?.some((instrument) =>
        instrumentFilter.includes(instrument)
      );
      if (!hasMatchingInstrument) return false;
    }

    return true;
  });

  const selectedInstrumentCount = instrumentFilter.length;

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest("PATCH", `/api/performers/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      // Just invalidate the query - the useEffect will handle sorting
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update performer",
        variant: "destructive",
      });
    },
  });

  const updateParticipantMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: { lookingForBand?: boolean };
    }) => {
      const response = await apiRequest("PATCH", `/api/participants/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      // Just invalidate the query - the useEffect will handle sorting
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update participant",
        variant: "destructive",
      });
    },
  });

  const handleFieldUpdate = (
    performerId: number,
    field: keyof Performer,
    value: any
  ) => {
    updatePerformerMutation.mutate({
      id: performerId,
      updates: { [field]: value },
    });
  };

  const handleParticipantFieldUpdate = (
    participantId: number,
    field: 'lookingForBand',
    value: boolean
  ) => {
    updateParticipantMutation.mutate({
      id: participantId,
      updates: { [field]: value },
    });
  };

  const handleInstrumentToggle = (
    performerId: number,
    instrument: Instrument,
    currentInstruments: string[],
  ) => {
    const newInstruments = currentInstruments.includes(instrument)
      ? currentInstruments.filter((i) => i !== instrument)
      : [...currentInstruments, instrument];

    handleFieldUpdate(performerId, "instruments", newInstruments);
  };

  if (isLoading) {
    return (
      <PageBackground className="flex items-center justify-center">
        <div className="text-center relative z-10">
          <div className="animate-spin h-8 w-8 mx-auto mb-4 border-4 border-slate-700 border-t-transparent rounded-full drop-shadow-sm" />
          <p className="text-slate-700 drop-shadow-sm">Loading participants...</p>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <div className="container mx-auto py-6 px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="flex items-center justify-between mb-8">
        <Button
          variant="ghost"
          className="flex items-center gap-2 bg-white/40 backdrop-blur-md border border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
          style={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
          onClick={() => setLocation(`/event/${jamNightId}`)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          variant="outline"
          onClick={handleDownloadCSV}
          className="flex items-center gap-2 bg-blue-100/40 backdrop-blur-md border-blue-200/50 text-blue-700 hover:bg-blue-100/60 hover:border-blue-300/60 shadow-lg transition-all duration-300"
          style={{
            boxShadow: '0 4px 16px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}
        >
          <Download className="h-4 w-4" />
          Download CSV
        </Button>
      </div>

      <ParticipantMetrics performers={sortedPerformers} />

      <div className="bg-white/30 rounded-2xl border border-white/30 overflow-hidden relative">
        <div className="px-6 py-4 border-b border-white/20 relative z-10">
          <div>
            <h2 className="text-lg font-semibold text-slate-900/90 flex items-center drop-shadow-sm">
              <Music className="h-5 w-5 mr-2 text-blue-600 drop-shadow-sm" />
              All Participant Data
              <span className="ml-2 text-sm font-normal text-slate-600/80 drop-shadow-sm">
                ({filteredPerformers.length})
              </span>
            </h2>
            <p className="text-sm text-slate-600/80 mt-1 drop-shadow-sm">
              View and edit detailed information for all participants
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 bg-white/10 border-b border-white/20 relative z-10">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search participants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs"
              />
            </div>

            <div className="flex gap-2">
              <Select value={playedFilter} onValueChange={setPlayedFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="played">Played</SelectItem>
                  <SelectItem value="not-played">Not Played</SelectItem>
                </SelectContent>
              </Select>

              <Popover open={showFilters} onOpenChange={setShowFilters}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="relative">
                    <Music className="h-4 w-4 mr-2" />
                    Role
                    {selectedInstrumentCount > 0 && (
                      <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                        {selectedInstrumentCount}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56" align="end">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Filter by Instruments</h4>
                    {instrumentOptions.map((instrument) => (
                      <div
                        key={instrument}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={instrument}
                          checked={instrumentFilter.includes(instrument)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setInstrumentFilter([...instrumentFilter, instrument]);
                            } else {
                              setInstrumentFilter(
                                instrumentFilter.filter((i) => i !== instrument)
                              );
                            }
                            // Close the popover after selection
                            setShowFilters(false);
                          }}
                        />
                        <label
                          htmlFor={instrument}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
                        >
                          {instrument}
                        </label>
                      </div>
                    ))}
                    {instrumentFilter.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setInstrumentFilter([])}
                        className="w-full"
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <div className="flex items-center space-x-2 px-3 py-2 border rounded-md h-10">
                <Switch
                  id="priority-filter"
                  checked={showPriorityOnly}
                  onCheckedChange={setShowPriorityOnly}
                />
                <label
                  htmlFor="priority-filter"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Priority
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile View */}
        <div className="block md:hidden">
          {filteredPerformers.map((performer) => (
            <div
              key={performer.id}
              className="border-b border-white/20 bg-white/10 p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <Input
                  value={performer.name}
                  onChange={(e) =>
                    handleFieldUpdate(performer.id, "name", e.target.value)
                  }
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditModalOpen(performer.id)}
                  className="flex items-center gap-2 bg-white/30 border border-white/20 hover:bg-white/40"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPerformers.map((performer) => (
                <TableRow key={performer.id}>
                  <TableCell>
                    <Input
                      value={performer.name}
                      onChange={(e) =>
                        handleFieldUpdate(performer.id, "name", e.target.value)
                      }
                      className="max-w-[400px]"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditModalOpen(performer.id)}
                      className="flex items-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Edit Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Edit Details Modals */}
        {filteredPerformers.map((performer) => (
          <ParticipantEditModal
            key={`edit-${performer.id}`}
            performer={performer}
            open={editModalOpen === performer.id}
            onOpenChange={(open) => setEditModalOpen(open ? performer.id : null)}
            jamNightId={jamNightId}
          />
        ))}

        {sortedPerformers.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Music className="h-8 w-8 mx-auto mb-4 opacity-50" />
            <p>No participants yet. Add someone to get started!</p>
          </div>
        ) : filteredPerformers.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Filter className="h-8 w-8 mx-auto mb-4 opacity-50" />
            <p>No participants match the selected filters.</p>
            <Button
              variant="outline"
              onClick={() => {
                setPlayedFilter("all");
                setInstrumentFilter([]);
                setShowPriorityOnly(false);
                setSearchTerm("");
              }}
              className="mt-2"
            >
              Clear Filters
            </Button>
          </div>
        ) : null}
      </div>
      </div>
    </PageBackground>
  );
} 